import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import Pagination from '../components/Pagination';
import './AdminManagement.css';

const AdminProjects = () => {
    const [projects, setProjects] = useState([]);
    const [editingId, setEditingId] = useState(null);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        status: '',
        metricLabel: '',
        metricValue: '',
        icon: '🚀',
        tech: '',
        link: '',
        github: '',
        order: 0
    });

    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalPages: 1
    });
    const token = localStorage.getItem('adminToken');

    const fetchProjects = React.useCallback(async (page = 1) => {
        try {
            const response = await axios.get(`/api/projects?page=${page}&limit=10`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setProjects(response.data.data);
            setPagination({
                currentPage: response.data.currentPage,
                totalPages: response.data.totalPages
            });
        } catch (err) {
            console.error('Fetch error:', err);
            setError('Failed to load projects.');
        }
    }, [token]);

    const handleDelete = React.useCallback(async (id) => {
        if (!window.confirm('Are you sure you want to delete this project?')) return;
        setError('');
        setSuccess('');
        try {
            await axios.delete(`/api/projects/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setSuccess('Project deleted successfully!');
            fetchProjects(pagination.currentPage);
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            setError(err.response?.data?.error || 'Delete failed.');
        }
    }, [token, fetchProjects, pagination.currentPage]);

    useEffect(() => {
        fetchProjects(1);
    }, [fetchProjects]);

    const handleInputChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        try {
            if (editingId) {
                await axios.put(`/api/projects/${editingId}`, formData, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setSuccess('Project updated successfully!');
            } else {
                await axios.post('/api/projects', formData, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setSuccess('Project created successfully!');
            }
            setFormData({
                name: '',
                description: '',
                status: '',
                metricLabel: '',
                metricValue: '',
                icon: '🚀',
                tech: '',
                link: '',
                github: '',
                order: 0
            });
            setEditingId(null);
            fetchProjects();
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            setError(err.response?.data?.error || 'Operation failed. Please check your inputs.');
        }
    };

    const startEdit = (project) => {
        setError('');
        setEditingId(project._id);
        setFormData({
            name: project.name,
            description: project.description,
            status: project.status || '',
            metricLabel: project.metricLabel || '',
            metricValue: project.metricValue || '',
            icon: project.icon || '🚀',
            tech: project.tech || '',
            link: project.link || '',
            github: project.github || '',
            order: project.order || 0
        });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    return (
        <div className="admin-manage-page">
            <header className="manage-header">
                <h1>Navigate Projects</h1>
                <p>{editingId ? 'Edit Project' : 'Add New Project'}</p>
            </header>

            <div className="manage-grid-layout">
                <form onSubmit={handleSubmit} className="manage-form-card">
                    {error && <div className="login-error" style={{ marginBottom: '1rem' }}>{error}</div>}
                    {success && (
                        <div style={{
                            background: 'rgba(16, 185, 129, 0.1)',
                            border: '1px solid rgba(16, 185, 129, 0.2)',
                            color: '#6ee7b7',
                            padding: '0.8rem',
                            borderRadius: '10px',
                            marginBottom: '1rem',
                            textAlign: 'center',
                            fontSize: '0.85rem'
                        }}>
                            {success}
                        </div>
                    )}

                    <div className="form-group">
                        <label>Project Name</label>
                        <input name="name" value={formData.name} onChange={handleInputChange} required />
                    </div>
                    <div className="form-group">
                        <label>Description</label>
                        <textarea name="description" value={formData.description} onChange={handleInputChange} required />
                    </div>
                    <div className="form-group">
                        <label>Status (e.g., Active, Completed, Beta)</label>
                        <input name="status" value={formData.status} onChange={handleInputChange} required />
                    </div>
                    <div className="form-group">
                        <label>Metric Label (e.g., Accuracy, Downloads)</label>
                        <input name="metricLabel" value={formData.metricLabel} onChange={handleInputChange} required />
                    </div>
                    <div className="form-group">
                        <label>Metric Value (e.g., 99.9%, 1.2M)</label>
                        <input name="metricValue" value={formData.metricValue} onChange={handleInputChange} required />
                    </div>
                    <div className="form-group">
                        <label>Icon Emoji</label>
                        <input name="icon" value={formData.icon} onChange={handleInputChange} placeholder="🚀" />
                    </div>
                    <div className="form-group">
                        <label>Tech Stack (comma separated)</label>
                        <input name="tech" value={formData.tech} onChange={handleInputChange} />
                    </div>
                    <div className="form-group">
                        <label>Demo Link</label>
                        <input name="link" value={formData.link} onChange={handleInputChange} />
                    </div>
                    <div className="form-group">
                        <label>GitHub Link</label>
                        <input name="github" value={formData.github} onChange={handleInputChange} />
                    </div>
                    <div className="form-group">
                        <label>Display Order</label>
                        <input type="number" name="order" value={formData.order} onChange={handleInputChange} />
                    </div>
                    <div className="form-actions">
                        <button type="submit" className="save-btn">{editingId ? 'Update' : 'Create'}</button>
                        {editingId && <button type="button" onClick={() => setEditingId(null)} className="cancel-btn">Cancel</button>}
                    </div>
                </form>

                <div className="items-list-section">
                    <h2>Active Projects</h2>
                    <div className="items-scroll-list">
                        <AnimatePresence>
                            {projects.map((p) => (
                                <motion.div
                                    key={p._id}
                                    className="item-row-card"
                                    initial={{ opacity: 0, x: 20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: -20 }}
                                >
                                    <div className="item-main">
                                        <h4>{p.name}</h4>
                                        <p>{p.description.substring(0, 60)}...</p>
                                    </div>
                                    <div className="item-actions">
                                        <button onClick={() => startEdit(p)} className="edit-btn">Edit</button>
                                        <button onClick={() => handleDelete(p._id)} className="delete-btn">Delete</button>
                                    </div>
                                </motion.div>
                            ))}
                        </AnimatePresence>
                    </div>

                    <Pagination
                        currentPage={pagination.currentPage}
                        totalPages={pagination.totalPages}
                        onPageChange={fetchProjects}
                    />
                </div>
            </div>
        </div>
    );
};

export default AdminProjects;
